import React, { memo, useCallback, useMemo } from 'react'

import { FiltersSidebar, Icon } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool } from 'prop-types'

import { FILTER_NAME } from 'Pods/SearchPage/Shared/Tracking/constants'
import { useTrackFilterSelect } from 'Pods/SearchPage/Shared/Tracking/FilterTracking'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import { addQuality, removeQuality, setContentSource } from 'Pods/SearchPage/Store/actions/qualityContent'
import { DEFAULT_STATE, QUALITY_LEVEL } from 'Pods/SearchPage/Store/constants'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { qualityLevels, contentSources } from './constants'

const DEFAULT_QUALITY = [QUALITY_LEVEL.ANY]

const buildOptions = (translate, keyspace, items) => items.map((item) => ({
  value: item.value,
  label: item.icon
    ? (
        <span className="filter-wrapper__option--with-icon">
          <Icon notSpaced svgComponent={item.icon} color="primary" size="small" />
          <span>{translate(`search_page.filters.quality.${keyspace}.${item.labelKey}`)}</span>
        </span>
      )
    : translate(`search_page.filters.quality.${keyspace}.${item.labelKey}`),
}))

// hideQualityLabel drops only the Quality group's title/tooltip when an external title already
// names the group (desktop md overflow, mobile collapsible header). Content source keeps its label.
function QualityContent({ hideQualityLabel = false }) {
  const translate = useTranslate()

  const quality = useSearchPageStore(state => state.quality ?? DEFAULT_QUALITY)
  const contentSource = useSearchPageStore(state => state.contentSource ?? DEFAULT_STATE.contentSource)
  const trackFilterSelect = useTrackFilterSelect()

  const handleQualityFilterChange = useCallback((_, value, isOn) => {
    if (isOn) {
      trackFilterSelect(FILTER_NAME.QUALITY, value)
      addQuality(value)
    } else {
      removeQuality(value)
    }
  }, [trackFilterSelect])

  const handleContentSourceFilterChange = useCallback((_, value) => {
    trackFilterSelect(FILTER_NAME.CONTENT_SOURCE, value)
    setContentSource(value)
  }, [trackFilterSelect])

  const qualityConfig = useMemo(() => [{
    label: hideQualityLabel ? '' : translate('search_page.filters.quality.title'),
    // Copy is from the Figma design and pending review with designers (see RailsEn).
    tooltip: hideQualityLabel ? undefined : translate('search_page.filters.quality.tooltip'),
    tooltipPlacement: 'bottom',
    tooltipContainerClassName: 'quality-filter__tooltip',
    filters: buildOptions(translate, 'levels', qualityLevels),
  }], [hideQualityLabel, translate])

  const contentSourceConfig = useMemo(() => [{
    label: translate('search_page.filters.quality.content_source_title'),
    filters: buildOptions(translate, 'content_source', contentSources),
  }], [translate])

  const selectedContentSource = useMemo(() => [contentSource], [contentSource])

  return (
    <>
      <FiltersSidebar
        isCollapsible={false}
        filters={qualityConfig}
        selectedFilters={quality}
        onFiltersChange={handleQualityFilterChange}
        isControlled
        useNewCheckboxes
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
      />
      <FiltersSidebar
        isCollapsible={false}
        filters={contentSourceConfig}
        selectedFilters={selectedContentSource}
        onFiltersChange={handleContentSourceFilterChange}
        hideLastDivider
        hideShowMore
        baseFormWrapperClassName="filter-wrapper__content"
        useRadio
      />
    </>
  )
}

QualityContent.propTypes = {
  hideQualityLabel: bool,
}

export default memo(QualityContent)
